/**
 * React Query hooks for customers data
 * Provides data fetching, caching, and mutations for customers
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  customersApi,
  type Customer,
  type CustomerSearchParams,
  type CreateCustomerData,
  type UpdateCustomerData,
  type CustomerVehicle,
  type CustomerActivity,
  type CustomerMetrics,
  type CustomerActivitySummary,
} from '../services/customersApi';
import { useDebouncedValue } from './useDebouncedValue';

/**
 * Query keys for customers
 */
export const customerKeys = {
  all: ['customers'] as const,
  lists: () => [...customerKeys.all, 'list'] as const,
  list: (params: CustomerSearchParams) => [...customerKeys.lists(), params] as const,
  search: (query: string) => [...customerKeys.all, 'search', query] as const,
  details: () => [...customerKeys.all, 'detail'] as const,
  detail: (id: string) => [...customerKeys.details(), id] as const,
  vehicles: (customerId: string) => [...customerKeys.all, 'vehicles', customerId] as const,
  activity: (customerId: string) => [...customerKeys.all, 'activity', customerId] as const,
};

/**
 * Hook to fetch paginated list of customers
 */
export function useCustomerList(params: CustomerSearchParams = {}) {
  return useQuery({
    queryKey: customerKeys.list(params),
    queryFn: () => customersApi.list(params),
    staleTime: 30_000,
  });
}

/**
 * Hook for customer typeahead search
 * Returns debounced search results for autocomplete
 */
export function useCustomerSearch(query: string) {
  const debouncedQuery = useDebouncedValue(query, 300);

  return useQuery({
    queryKey: customerKeys.search(debouncedQuery),
    queryFn: () => customersApi.search(debouncedQuery),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
  });
}

/**
 * Hook to fetch a single customer by ID
 */
export function useCustomer(id: string | undefined) {
  return useQuery({
    queryKey: customerKeys.detail(id!),
    queryFn: () => customersApi.getById(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

/**
 * Hook to fetch customer's vehicles
 */
export function useCustomerVehicles(customerId: string | undefined) {
  return useQuery({
    queryKey: customerKeys.vehicles(customerId!),
    queryFn: () => customersApi.getVehicles(customerId!),
    enabled: !!customerId,
    staleTime: 30_000,
  });
}

/**
 * Hook to fetch customer activity summary (metrics + timeline)
 */
export function useCustomerActivity(customerId: string | undefined, limit = 20) {
  return useQuery({
    queryKey: customerKeys.activity(customerId!),
    queryFn: () => customersApi.getActivity(customerId!, limit),
    enabled: !!customerId,
    staleTime: 60_000, // Activity data can be cached longer
  });
}

/**
 * Hook to create a new customer
 */
export function useCreateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCustomerData) => customersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
    },
  });
}

/**
 * Hook to update a customer
 */
export function useUpdateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCustomerData }) =>
      customersApi.update(id, data),
    onSuccess: (customer) => {
      queryClient.setQueryData(customerKeys.detail(customer.id), customer);
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
    },
  });
}

/**
 * Hook to delete a customer
 */
export function useDeleteCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => customersApi.delete(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: customerKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
    },
  });
}

// Re-export types for convenience
export type {
  Customer,
  CustomerVehicle,
  CustomerSearchParams,
  CreateCustomerData,
  UpdateCustomerData,
  CustomerActivity,
  CustomerMetrics,
  CustomerActivitySummary,
};
